import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { telecallers } from '../../services/dealerApi';
import { BarChart3, MessageCircle, TrendingUp, Loader2, CheckCircle2, Headphones, Users, CalendarClock } from 'lucide-react';
import BillingCard from '../../components/dealers/BillingCard';
import TelecallerManager from '../../components/dealers/TelecallerManager';
import MyDay from './MyDay';

const TABS = [
  { key: 'team', label: 'Team & Billing', icon: Users },
  { key: 'my-day', label: 'My Day', icon: CalendarClock },
  { key: 'performance', label: 'Performance', icon: BarChart3 },
];

export default function TelecallersPage() {
  // If the current actor is a telecaller, DealerLayout will redirect them
  // away — render nothing meanwhile so their child API calls do NOT fire
  // and flash a 403 error toast.
  const ctx = useOutletContext() || {};
  if (ctx.actorType === 'telecaller') return null;

  // Preserve tab across reloads and enable deep-linking via ?tab=my-day
  const initial = new URLSearchParams(window.location.search).get('tab') || 'team';
  return <TelecallersPageInner initialTab={TABS.some((t) => t.key === initial) ? initial : 'team'} />;
}

function TelecallersPageInner({ initialTab }) {
  const [tab, setTab] = useState(initialTab);

  const switchTab = (key) => {
    setTab(key);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', key);
    window.history.replaceState({}, '', url.toString());
  };

  return (
    <div className="space-y-6" data-testid="telecallers-page">
      <header>
        <div className="inline-flex items-center gap-2 text-cyan-400 text-xs uppercase tracking-[0.25em] font-bold">
          <Headphones className="w-3.5 h-3.5" /> Team
        </div>
        <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mt-1">Telecallers</h1>
        <p className="text-slate-400 text-sm mt-1">
          Manage sub-accounts, run your call day, and track pipeline performance — all in one place.
        </p>
      </header>

      {/* Tab bar */}
      <div className="flex flex-wrap gap-1 border-b border-slate-800">
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => switchTab(t.key)}
              data-testid={`telecallers-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                active
                  ? 'border-cyan-500 text-cyan-300'
                  : 'border-transparent text-slate-400 hover:text-white'
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab body */}
      {tab === 'team' && <TeamTab />}
      {tab === 'my-day' && (
        <div data-testid="telecallers-tab-body-my-day">
          <MyDay />
        </div>
      )}
      {tab === 'performance' && <PerformanceTab />}
    </div>
  );
}

// ---- Team tab: billing + manager (uses onChange to trigger re-render only) ----
function TeamTab() {
  const [refreshTick, setRefreshTick] = useState(0);
  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="telecallers-tab-body-team">
      <div className="lg:col-span-1"><BillingCard /></div>
      <div className="lg:col-span-2">
        <TelecallerManager onChange={() => setRefreshTick((t) => t + 1)} key={refreshTick} />
      </div>
    </div>
  );
}

// ---- Performance tab ----
function PerformanceTab() {
  const [perf, setPerf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    telecallers.performance()
      .then((r) => setPerf(r))
      .catch((err) => setError(err.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 text-cyan-500 animate-spin" /></div>;
  }
  if (error) {
    return <div className="p-4 rounded-2xl bg-red-500/10 border border-red-500/30 text-red-200 text-sm" data-testid="perf-error">{error}</div>;
  }

  const overall = perf?.overall || {};
  const rows = perf?.rows || [];

  return (
    <div data-testid="telecallers-tab-body-performance">
      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <SummaryTile label="Total Leads Uploaded" value={overall.leads_uploaded || 0} icon={BarChart3} />
        <SummaryTile label="Leads Contacted (WhatsApp)" value={overall.leads_contacted || 0} icon={MessageCircle} />
        <SummaryTile label="Response Rate" value={`${Math.round(((overall.response_rate || 0) * 100))}%`} icon={TrendingUp} />
      </div>

      <div className="rounded-2xl border border-slate-800 overflow-hidden">
        <table className="w-full text-sm" data-testid="performance-table">
          <thead className="bg-slate-900/60 text-slate-400 uppercase text-xs">
            <tr>
              <th className="px-4 py-3 text-left">Telecaller</th>
              <th className="px-4 py-3 text-left">Source</th>
              <th className="px-4 py-3 text-right">Leads Uploaded</th>
              <th className="px-4 py-3 text-right">Contacted</th>
              <th className="px-4 py-3 text-right">Response Rate</th>
              <th className="px-4 py-3 text-center">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {rows.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">No data yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.telecaller_id || 'main'} className="hover:bg-white/[0.02]" data-testid={`perf-row-${r.telecaller_id || 'main'}`}>
                <td className="px-4 py-3">
                  <div className="text-white font-semibold">{r.name}</div>
                  <div className="text-xs text-slate-500">{r.email}</div>
                </td>
                <td className="px-4 py-3 text-slate-300">{r.source_tag || <span className="text-slate-600">—</span>}</td>
                <td className="px-4 py-3 text-right text-white font-semibold">{r.leads_uploaded}</td>
                <td className="px-4 py-3 text-right text-slate-300">{r.leads_contacted}</td>
                <td className="px-4 py-3 text-right text-cyan-300 font-semibold">
                  {Math.round((r.response_rate || 0) * 100)}%
                </td>
                <td className="px-4 py-3 text-center">
                  {r.is_active ? (
                    <span className="inline-flex items-center gap-1 text-emerald-400 text-xs"><CheckCircle2 className="w-3 h-3" /> Active</span>
                  ) : (
                    <span className="text-xs text-slate-500">Inactive</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, icon: Icon }) {
  return (
    <div className="p-4 rounded-2xl bg-slate-900/40 border border-slate-800">
      <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider">
        <Icon className="w-4 h-4 text-cyan-400" /> {label}
      </div>
      <div className="mt-2 text-3xl font-extrabold text-white tabular-nums">{value}</div>
    </div>
  );
}
